import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
const apiUrl = import.meta.env.VITE_API_URL;
// Define VideoJob interface (same as in VideoJobsPage)
interface VideoJob {
  id: string;
  project_id: string;
  status: "queued" | "processing" | "completed" | "failed";
  current_step: string | null;
  progress: number;
  error_message: string | null;
  logs: Array<{
    timestamp: string;
    message: string;
  }>;
  result_video_url: string | null;
  created_at: string;
  updated_at: string;
}

export default function VideoJobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Try to get initial job data from location state
  const initialJob = location.state?.job as VideoJob | undefined;

  const [job, setJob] = useState<VideoJob | null>(initialJob || null);
  // Always start with loading state true to ensure we fetch fresh data
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isLiveUpdating, setIsLiveUpdating] = useState(false);

  // Fetch job data with direct axios call using token
  const fetchJobData = useCallback(async () => {
    if (!jobId || !token) return;

    // Always set loading true when explicitly fetching
    setIsLoading(true);

    try {
      console.log(`Fetching job data for ${jobId}...`);
      const response = await axios.get(`${apiUrl}/api/videos/jobs/${jobId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const newJobData = response.data;
      console.log(
        `Received job data: status=${newJobData.status}, progress=${newJobData.progress}%`
      );

      // Only update if there's a change to avoid unnecessary renders
      setJob((prev) => {
        if (
          !prev ||
          prev.progress !== newJobData.progress ||
          prev.status !== newJobData.status ||
          prev.current_step !== newJobData.current_step
        ) {
          return newJobData;
        }
        return prev;
      });

      setIsLoading(false);

      return newJobData;
    } catch (err: any) {
      console.error("Error fetching job details:", err);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError("Authentication failed. Please log in again.");
      } else {
        setError(err.response?.data?.detail || "Failed to load job details");
        toast.error("Error loading job details");
      }
      setIsLoading(false);
      return null;
    }
  }, [jobId, token]);

  // Start polling for updates
  const startPolling = useCallback(() => {
    // Stop any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Start a new polling interval
    const intervalId = setInterval(async () => {
      console.log("Polling for job updates...");
      const updatedJob = await fetchJobData();

      // Only stop polling if job is completed or failed
      if (
        updatedJob &&
        (updatedJob.status === "completed" || updatedJob.status === "failed")
      ) {
        console.log(
          `Job ${updatedJob.id} is ${updatedJob.status}, stopping polling`
        );
        clearInterval(intervalId);
        pollingIntervalRef.current = null;
        setIsLiveUpdating(false);
      }
    }, 2000); // Poll every 2 seconds

    pollingIntervalRef.current = intervalId;
    setIsLiveUpdating(true);

    return () => {
      clearInterval(intervalId);
      pollingIntervalRef.current = null;
      setIsLiveUpdating(false);
    };
  }, [fetchJobData]);

  // Initialize polling on component mount
  useEffect(() => {
    if (!token || !jobId) return;

    // Always fetch fresh data on mount, regardless of initialJob
    console.log("Component mounted, fetching fresh job data");
    setIsLoading(true);

    fetchJobData().then((jobData) => {
      // Start polling if job is processing OR queued (to catch transitions)
      if (
        jobData &&
        (jobData.status === "processing" || jobData.status === "queued")
      ) {
        console.log(
          `Job is in ${jobData.status} state with ${jobData.progress}% progress, starting polling to watch for updates`
        );
        startPolling();
      } else if (jobData) {
        console.log(`Job is in ${jobData.status} state, not starting polling`);
      }
    });

    // Cleanup function
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [jobId, token, fetchJobData, startPolling]);

  // Format timestamp to readable format
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  // Format step name to be more readable
  const formatStepName = (stepName: string | null) => {
    if (!stepName) return "";
    return stepName
      .replace(/_/g, " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Extract actor ID from job logs
  const extractActorId = (job: VideoJob | null): string | null => {
    if (!job || !job.logs || job.logs.length === 0) return null;

    // Look for actor ID in logs
    // Try to find log entries about processing a specific actor
    for (const log of job.logs) {
      // Check initial log entry - "Creating video job for specific actor: {actor_id}"
      const creatingMatch = log.message.match(
        /Creating video job for specific actor: ([a-zA-Z0-9_-]+)/
      );
      if (creatingMatch && creatingMatch[1]) {
        return creatingMatch[1];
      }

      // Check processing log entry - "Starting video generation process for actor: {actor_id}"
      const startingMatch = log.message.match(
        /Starting video generation process for actor: ([a-zA-Z0-9_-]+)/
      );
      if (startingMatch && startingMatch[1]) {
        return startingMatch[1];
      }

      // Check other actor processing messages - "STARTING VIDEO GENERATION FOR ACTOR: {actor_id}"
      const capstartMatch = log.message.match(
        /STARTING VIDEO GENERATION FOR ACTOR: ([a-zA-Z0-9_-]+)/
      );
      if (capstartMatch && capstartMatch[1]) {
        return capstartMatch[1];
      }

      // Check processing for actor message - "Processing video for actor: (.+) \(ID: ([a-zA-Z0-9_-]+)\)"
      const processingMatch = log.message.match(
        /Processing video for actor: .+? \(ID: ([a-zA-Z0-9_-]+)\)/
      );
      if (processingMatch && processingMatch[1]) {
        return processingMatch[1];
      }
    }

    return null;
  };

  // Extract actor name from job logs if available
  const extractActorName = (job: VideoJob | null): string | null => {
    if (!job || !job.logs || job.logs.length === 0) return null;

    for (const log of job.logs) {
      const processingMatch = log.message.match(
        /Processing video for actor: (.+?) \(ID: [a-zA-Z0-9_-]+\)/
      );
      if (processingMatch && processingMatch[1]) {
        return processingMatch[1];
      }
    }

    return null;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-5 flex items-center justify-center h-64">
        <div className="flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600">Loading job data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-5">
        <div className="bg-red-50 p-4 rounded-md text-red-800">
          <h2 className="text-xl font-bold mb-2">Error</h2>
          <p>{error}</p>
          <button
            onClick={() => navigate("/video-jobs")}
            className="mt-4 px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container mx-auto p-5">
        <div className="bg-gray-50 p-4 rounded-md text-gray-800">
          <h2 className="text-xl font-bold mb-2">Job Not Found</h2>
          <p>The requested video job could not be found.</p>
          <button
            onClick={() => navigate("/video-jobs")}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  const actorId = extractActorId(job);
  const actorName = extractActorName(job);

  return (
    <div className="container mx-auto p-5">
      <Toaster position="top-right" />

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Video Job Details</h1>
        <div>
          <button
            onClick={() => navigate("/video-jobs")}
            className="px-4 py-2 mr-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Back to Jobs
          </button>
          <button
            onClick={() => {
              fetchJobData().then((jobData) => {
                if (
                  jobData &&
                  jobData.status === "processing" &&
                  !isLiveUpdating
                ) {
                  startPolling();
                  toast.success("Live updates activated");
                }
              });
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        {/* Actor ID Header */}
        {actorId && (
          <div className="p-4 bg-purple-50 border-b border-purple-100">
            <div className="flex items-center">
              <div className="mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-purple-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="font-medium text-purple-800">
                  Processing Avatar
                </h3>
                <div className="flex items-center">
                  <span className="font-mono text-purple-900 bg-purple-100 px-2 py-1 rounded text-sm mr-2">
                    {actorId}
                  </span>
                  {actorName && (
                    <span className="text-purple-700 text-sm">
                      ({actorName})
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Job Header */}
        <div className="p-5 border-b">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium
                  ${
                    job.status === "queued"
                      ? "bg-blue-100 text-blue-800"
                      : job.status === "processing"
                      ? "bg-yellow-100 text-yellow-800"
                      : job.status === "completed"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                </span>
                {job.status === "processing" && (
                  <div className="ml-2 animate-pulse flex space-x-1">
                    <div className="w-1 h-1 bg-yellow-800 rounded-full"></div>
                    <div className="w-1 h-1 bg-yellow-800 rounded-full"></div>
                    <div className="w-1 h-1 bg-yellow-800 rounded-full"></div>
                  </div>
                )}
                {job.status === "queued" && (
                  <div className="ml-2 flex items-center">
                    <span className="text-xs text-blue-800 italic">
                      Waiting to start...
                    </span>
                  </div>
                )}
                {isLiveUpdating && (
                  <span className="ml-2 text-xs text-green-500 font-medium">
                    Live updates active
                  </span>
                )}
              </div>
              <h2 className="mt-2 text-xl font-bold">Job: {job.id}</h2>
              <p className="text-gray-600 text-sm">Project: {job.project_id}</p>
            </div>
            <div className="text-right text-sm text-gray-500">
              Started: {new Date(job.created_at).toLocaleString()}
              <br />
              Last Updated: {new Date(job.updated_at).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Progress Section */}
        {job.status === "processing" && (
          <div className="p-5 border-b bg-gray-50">
            <div className="mb-2 flex justify-between items-center">
              <h3 className="font-semibold text-md text-gray-700">
                {formatStepName(job.current_step)}
              </h3>
              <span className="text-gray-700 font-bold">{job.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-in-out"
                style={{ width: `${job.progress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Completed Section */}
        {job.status === "completed" && job.result_video_url && (
          <div className="p-5 border-b bg-green-50">
            <h3 className="font-semibold text-lg text-green-700 mb-3">
              Video Ready
            </h3>
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
              <a
                href={job.result_video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-center"
              >
                Watch Video
              </a>
              <a
                href={job.result_video_url}
                download
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-center"
              >
                Download Video
              </a>
            </div>
          </div>
        )}

        {/* Error Section */}
        {job.status === "failed" && (
          <div className="p-5 border-b bg-red-50">
            <h3 className="font-semibold text-lg text-red-700 mb-2">
              Processing Failed
            </h3>
            <div className="p-3 bg-white rounded border border-red-200">
              <p className="text-red-800">
                {job.error_message ||
                  "An unknown error occurred during video processing."}
              </p>
            </div>
          </div>
        )}

        {/* Logs Section */}
        <div className="p-5">
          <h3 className="font-semibold text-lg mb-3">Processing Logs</h3>

          <div className="bg-gray-900 text-gray-100 rounded p-4 overflow-auto max-h-96 font-mono text-sm">
            {job.logs && job.logs.length > 0 ? (
              job.logs.map((log, index) => (
                <div key={index} className="mb-1">
                  <span className="text-gray-400">
                    [{formatTimestamp(log.timestamp)}]
                  </span>{" "}
                  <span className="text-green-400">{log.message}</span>
                </div>
              ))
            ) : (
              <p className="text-gray-500 italic">No logs available yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
